import { Controller } from "@hotwired/stimulus"
import { filterBarParams } from "../lib/analytics_filters"

// Drives the merchant column-management editor (app/views/analytics/shared/_table_editor.html.erb).
// Column state lives on each <th> as data-* attributes (plus the label text input). Edits are a DRAFT:
// drag/rename/hide happen purely in the DOM (instant, nothing persisted); a format/link change previews
// via a non-persisting server render (reformatted cells swapped into the body). "Save" serializes the
// draft into the form, which POSTs and commits ONE new report version; "Cancel" reloads the view.
export default class extends Controller {
  static targets = [
    "header", "tbody", "payload", "modalTitle", "modalLabel", "modalValueFormat", "modalDecimalPlaces",
    "modalPrefix", "modalSuffix", "modalLinkEntity", "modalLinkIdColumn",
    "decimalWrap", "affixWrap", "prefixWrap", "suffixWrap", "prefixLabel", "linkWrap"
  ]
  static values = { previewUrl: String, viewUrl: String, baseMessageId: String }

  // --- reordering (live drag) ----------------------------------------------
  dragStart(e) {
    this.dragged = e.target.closest('[data-analytics-column-editor-target="header"]')
    if (!this.dragged) return
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move"
      e.dataTransfer.setData("text/plain", this.dragged.dataset.column)
      e.dataTransfer.setDragImage(this.dragged, 20, 20) // ghost shows the column being dragged
    }
    this.highlightColumn(this.dragged.dataset.column, true)
  }

  // Live: as the cursor passes a column, move the dragged column there immediately so the table shifts.
  dragOver(e) {
    e.preventDefault()
    const target = e.target.closest('[data-analytics-column-editor-target="header"]')
    if (!this.dragged || !target || target === this.dragged) return

    const draggedIsBefore = this.dragged.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING
    target.parentElement.insertBefore(this.dragged, draggedIsBefore ? target.nextSibling : target)
    this.syncBodyOrder()
  }

  drop(e) {
    e.preventDefault()
  }

  dragEnd() {
    if (this.dragged) this.highlightColumn(this.dragged.dataset.column, false)
    this.dragged = null
  }

  moveLeft(e) {
    this.moveHeader(e.target.closest('[data-analytics-column-editor-target="header"]'), true)
  }

  moveRight(e) {
    this.moveHeader(e.target.closest('[data-analytics-column-editor-target="header"]'), false)
  }

  moveHeader(header, left) {
    const sibling = left ? header?.previousElementSibling : header?.nextElementSibling
    if (!sibling) return
    header.parentElement.insertBefore(left ? header : sibling, left ? sibling : header)
    this.syncBodyOrder()
  }

  // Rename is a pure DOM draft — the label input's value is read at serialize()/Save time, so there's
  // no per-keystroke handler.

  // --- show / hide ----------------------------------------------------------
  toggleHidden(e) {
    const button = e.target.closest("button")
    const header = button.closest('[data-analytics-column-editor-target="header"]')
    const hidden = header.dataset.hidden !== "true"
    header.dataset.hidden = hidden ? "true" : "false"
    header.classList.toggle("opacity-50", hidden)
    this.cellsFor(header.dataset.column).forEach((td) => td.classList.toggle("opacity-50", hidden))

    // Keep the control's affordance in sync: icon + the announced/label action.
    const action = hidden ? "Show column" : "Hide column"
    button.setAttribute("aria-label", action)
    button.setAttribute("title", action)
    button.querySelector("[data-eye-hidden]")?.classList.toggle("hidden", !hidden)
    button.querySelector("[data-eye-visible]")?.classList.toggle("hidden", hidden)
  }

  // --- per-column config modal ---------------------------------------------
  openConfig(e) {
    this.activeHeader = e.target.closest('[data-analytics-column-editor-target="header"]')
    const h = this.activeHeader
    if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = h.dataset.column
    if (this.hasModalLabelTarget) this.modalLabelTarget.value = h.querySelector('input[type="text"]')?.value || ""
    if (this.hasModalValueFormatTarget) this.modalValueFormatTarget.value = h.dataset.valueFormat || ""
    if (this.hasModalDecimalPlacesTarget) this.modalDecimalPlacesTarget.value = h.dataset.decimalPlaces || ""
    if (this.hasModalPrefixTarget) this.modalPrefixTarget.value = h.dataset.prefix || ""
    if (this.hasModalSuffixTarget) this.modalSuffixTarget.value = h.dataset.suffix || ""
    if (this.hasModalLinkEntityTarget) this.modalLinkEntityTarget.value = h.dataset.linkEntity || ""
    if (this.hasModalLinkIdColumnTarget) this.modalLinkIdColumnTarget.value = h.dataset.linkIdColumn || ""
    this.formatChanged()
  }

  // Progressive disclosure: only show the fields the renderer actually honors for the chosen format.
  //  - decimal places: currency / percentage / decimal
  //  - prefix: currency (the currency symbol/unit) / decimal / integer / string
  //  - suffix: decimal / integer / string — NOT currency (number_to_currency ignores it) or percentage
  //  - link fields: only "Link to record"
  formatChanged() {
    const fmt = this.hasModalValueFormatTarget ? this.modalValueFormatTarget.value : ""
    const showPrefix = ["currency", "decimal", "integer", "string"].includes(fmt)
    const showSuffix = ["decimal", "integer", "string"].includes(fmt)

    this.toggle(this.decimalWrapTarget, ["currency", "percentage", "decimal"].includes(fmt))
    this.toggle(this.affixWrapTarget, showPrefix || showSuffix)
    this.toggle(this.prefixWrapTarget, showPrefix)
    this.toggle(this.suffixWrapTarget, showSuffix)
    // Currency's "prefix" is really the currency symbol, and it sits alone → give it the full row.
    if (this.hasPrefixWrapTarget) this.prefixWrapTarget.classList.toggle("col-span-2", showPrefix && !showSuffix)
    if (this.hasPrefixLabelTarget) this.prefixLabelTarget.textContent = fmt === "currency" ? "Currency symbol" : "Prefix"
    this.toggle(this.linkWrapTarget, fmt === "link")
  }

  applyConfig() {
    const h = this.activeHeader
    if (!h) return

    const labelInput = h.querySelector('input[type="text"]')
    if (labelInput && this.hasModalLabelTarget) labelInput.value = this.modalLabelTarget.value.trim()
    h.dataset.valueFormat = this.valueOf(this.modalValueFormatTarget)
    h.dataset.decimalPlaces = this.valueOf(this.modalDecimalPlacesTarget)
    h.dataset.prefix = this.valueOf(this.modalPrefixTarget)
    h.dataset.suffix = this.valueOf(this.modalSuffixTarget)
    h.dataset.linkEntity = this.valueOf(this.modalLinkEntityTarget)
    h.dataset.linkIdColumn = this.valueOf(this.modalLinkIdColumnTarget)
    this.preview() // reformats cell values against the draft — no persistence
  }

  // --- save / cancel --------------------------------------------------------
  // Serialize the draft into the Save form's hidden field just before it POSTs (commits a new version).
  serializeForSave() {
    this.payloadTarget.value = this.serialize()
  }

  cancel(e) {
    e.preventDefault()
    const frame = this.element.closest("turbo-frame")
    if (frame) frame.src = this.withFilters(this.viewUrlValue) // discard the draft, back to view mode
  }

  // --- preview (non-persisting body re-render) ------------------------------
  preview() {
    const requested = this.serialize()
    const body = new URLSearchParams()
    body.set("table_columns", requested)
    body.set("base_message_id", this.baseMessageIdValue)
    filterBarParams(body)

    fetch(this.previewUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken(),
        Accept: "text/html",
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    }).then((response) => {
      if (!response.ok) return null
      // Apply only if the draft still matches what this preview was rendered for — guards responses that
      // land out of order AND a drag/hide/format that mutated the draft while this request was in flight
      // (otherwise the old body would sit under the new headers). Re-checked after text() resolves.
      return response.text().then((html) => {
        if (this.serialize() === requested) this.swapBody(html)
      })
    })
  }

  // --- serialization --------------------------------------------------------
  // Returns the full ordered column list as a JSON string, read straight from the header DOM state.
  serialize() {
    const columns = this.headerTargets.map((h) => {
      const label = h.querySelector('input[type="text"]')?.value?.trim()
      const column = { column: h.dataset.column, hidden: h.dataset.hidden === "true" }
      if (label) column.label = label
      this.assign(column, "value_format", h.dataset.valueFormat)
      this.assign(column, "decimal_places", h.dataset.decimalPlaces)
      this.assign(column, "prefix", h.dataset.prefix)
      this.assign(column, "suffix", h.dataset.suffix)
      this.assign(column, "link_entity", h.dataset.linkEntity)
      this.assign(column, "link_id_column", h.dataset.linkIdColumn)
      return column
    })
    return JSON.stringify(columns)
  }

  swapBody(html) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const fresh = doc.querySelector('[data-analytics-column-editor-target="tbody"]')
    if (fresh && this.hasTbodyTarget) this.tbodyTarget.innerHTML = fresh.innerHTML
  }

  // --- helpers --------------------------------------------------------------
  // Reconcile each row's cells to the header order. `dragOver` calls this on every tick, so only move a
  // cell that is actually out of position (an already-ordered row does zero DOM work → no reflow); a
  // one-slot drag moves just the one displaced cell per row instead of re-appending them all.
  syncBodyOrder() {
    const order = this.headerTargets.map((h) => h.dataset.column)
    this.rows().forEach((row) => {
      const byColumn = {}
      row.querySelectorAll(":scope > td[data-col]").forEach((td) => (byColumn[td.dataset.col] = td))
      let next = row.firstElementChild
      order.forEach((col) => {
        const td = byColumn[col]
        if (!td) return
        if (td === next) next = td.nextElementSibling // already in place; advance the anchor
        else row.insertBefore(td, next) // move only the displaced cell, keeping `next` as the anchor
      })
    })
  }

  highlightColumn(column, on) {
    const header = this.headerTargets.find((h) => h.dataset.column === column)
    header?.classList.toggle("bg-violet-100", on)
    this.cellsFor(column).forEach((td) => td.classList.toggle("bg-violet-50", on))
  }

  cellsFor(column) {
    return Array.from(this.element.querySelectorAll("td[data-col]")).filter((td) => td.dataset.col === column)
  }

  rows() {
    return this.hasTbodyTarget ? Array.from(this.tbodyTarget.querySelectorAll(":scope > tr")) : []
  }

  withFilters(url) {
    const resolved = new URL(url, window.location.origin)
    filterBarParams(resolved.searchParams)
    return resolved.pathname + resolved.search
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  assign(obj, key, value) {
    if (value != null && value !== "") obj[key] = value
  }

  valueOf(target) {
    return target ? target.value.trim() : ""
  }

  toggle(el, show) {
    if (el) el.classList.toggle("hidden", !show)
  }
}
